"use client";

import React, { useState } from "react";
import Imperium from "@/public/icons/factions/imperium_of_man.svg";
import Chaos from "@/public/icons/factions/chaos.svg";
import Aeldari from "@/public/icons/factions/aeldari.svg";
import Orks from "@/public/icons/factions/orks.svg";
import Necrons from "@/public/icons/factions/necrons.svg";
import Tau from "@/public/icons/factions/tau.svg";
import Tyranids from "@/public/icons/factions/tyranids.svg";
import Drukhari from "@/public/icons/factions/drukhari.svg";
import LeaguesOfVotann from "@/public/icons/factions/leagues_of_votann.svg";
import { Group, GroupContent, Media } from "@/src/payload-types";
import { getBlobUrl } from "@/src/components/utils/utils";
import CodexFaction, { CodexListEntry } from "@/app/(app)/codex/CodexFaction";
import { usePathname, useSearchParams } from "next/navigation";

export type GroupWithSummary = Group & {summary?: Partial<GroupContent>};

const FactionSelection = ({
    groups,
    entries,
}: {
    groups: GroupWithSummary[];
    entries: CodexListEntry[];
}) => {
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const selection = searchParams.get("selection");

    const onFactionClick = (id: number) => {
        const params = new URLSearchParams(searchParams.toString());
        const faction = groups.find((g) => g.id === id);
        if (faction) {
            params.set("selection", faction.slug);
            // Only updates the URL; useSearchParams picks it up without a server round trip
            window.history.pushState(null, "", `${pathname}?${params.toString()}`);
        }
    };

    const currentGroup = groups.find((g) => g.slug === selection);
    let codexEntries, splash, image;

    if(currentGroup) {
        codexEntries = entries.filter((e) => e.group === currentGroup.id);

        splash = (currentGroup.splash as Media)?.sizes?.splash?.url
            ? (currentGroup.splash as Media)?.sizes?.splash
            : (currentGroup.splash as Media)?.sizes?.avif;
        image = splash && getBlobUrl(splash.url!);
    }

    return (
        <div className="w-3/4">
            <div className="flex items-center flex-wrap justify-center gap-4">
                {sortFactions(groups).map((g) => (
                    <FactionLogo key={g.id} onClick={onFactionClick} Logo={getLogo(g.codexLogo)} group={g} />
                ))}
            </div>
            {currentGroup && <CodexFaction
                key={currentGroup.id}
                group={currentGroup}
                image={image}
                splash={splash}
                entries={codexEntries}
            />}
        </div>
    );
};

const FactionLogo = ({ Logo, group, onClick }: {
    Logo: React.ComponentType<{ className?: string }>;
    group: Group;
    onClick: (id: number) => void;
}) => {
    return (
        <div onClick={() => onClick(group.id)} className="selection-logo w-20">
            <Logo className="h-20 w-20" />
            <span className="text-center block">{group.name}</span>
        </div>
    );
};

// Display order of the factions, by codexLogo; factions not listed go last
const FACTION_ORDER = [
    "imperium_of_man.svg",
    "chaos.svg",
    "orks.svg",
    "aeldari.svg",
    "drukhari.svg",
    "necrons.svg",
    "tyranids.svg",
    "tau.svg",
    "leagues_of_votann.svg",
];

const factionRank = (group: Group) => {
    const rank = FACTION_ORDER.indexOf(group.codexLogo ?? "");
    return rank === -1 ? FACTION_ORDER.length : rank;
};

const sortFactions = <T extends Group>(groups: T[]) => [...groups].sort((a, b) => factionRank(a) - factionRank(b));

const getLogo = (url?: string | null) => {
    switch (url) {
        case "imperium_of_man.svg":
            return Imperium;
        case "chaos.svg":
            return Chaos;
        case "aeldari.svg":
            return Aeldari;
        case "orks.svg":
            return Orks;
        // TODO: add dedicated SVGs for these factions
        case "necrons.svg":
            return Necrons;
        case "tau.svg":
            return Tau;
        case "tyranids.svg":
            return Tyranids;
        case "leagues_of_votann.svg":
            return LeaguesOfVotann;
        case "drukhari.svg":
            return Drukhari;
        default:
            return Imperium;
    }
};

export default FactionSelection;
